/**
 * What the Rush keeps on the device (feature-local KV keys, scoped to the signed-in player):
 * the last standing seen (the weekend's total for the results card: the API forgets a closed
 * Rush on Monday) and the server clock offset (countdowns follow server time, not the
 * device clock). Tiny and synchronous: the manifest reads it for the rail badge and map check.
 *
 * A group rank seen during the weekend is never shown as the result: only the server's
 * closing message (inbox, top 3 of each group) says who won, and it lands in `final`.
 */
import type { AppContext } from '../../game/context.js';

type Ctx = Pick<AppContext, 'services'>;

const LAST = 'feature.tournament.last';
const CLOCK = 'feature.tournament.clock';
const SEEN = 'feature.tournament.seen';

/** The player's standing in a Rush, as last seen (view or round answer). */
export interface RushStanding {
  playerId: string | null;
  /** tournament id (the Friday) */
  id: string;
  closesAt: number;
  total: number;
  /** live rank in the group of 100 when last fetched (0 = not known): never the final result */
  rank: number;
  /** players in the group */
  players: number;
  /** `services.now()` of the last standing fetch (0: a refresh is due) */
  fetchedAt: number;
  /** final group place from the server's closing inbox message (1–3; null: none or not known) */
  final: number | null;
  /** the results card was shown */
  shown: boolean;
}

export interface ClockOffset {
  /** server time − `services.now()` */
  offset: number;
  /** `services.now()` when measured */
  at: number;
}

function read<T>(a: Ctx, key: string): T | null {
  try {
    const raw = a.services.kv.get(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(a: Ctx, key: string, v: unknown): void {
  try {
    a.services.kv.set(key, JSON.stringify(v));
  } catch {
    /* storage full or blocked: the next load fetches again */
  }
}

export function playerId(a: Ctx): string | null {
  return a.services.store.state.auth?.playerId ?? null;
}

export function lastStanding(a: Ctx): RushStanding | null {
  const s = read<Partial<RushStanding>>(a, LAST);
  if (!s || typeof s.id !== 'string' || s.playerId !== playerId(a)) return null;
  // older saves lack the refresh time and the final place
  return { fetchedAt: 0, final: null, ...s } as RushStanding;
}

export function saveStanding(a: Ctx, s: Omit<RushStanding, 'playerId'>): void {
  write(a, LAST, { ...s, playerId: playerId(a) });
}

/** The player opened the Rush screen during this weekend (the rail dot goes). */
export function weekendSeen(a: Ctx, id: string): boolean {
  const s = read<{ playerId: string | null; id: string }>(a, SEEN);
  return !!s && s.id === id && s.playerId === playerId(a);
}

export function markWeekendSeen(a: Ctx, id: string): void {
  if (!weekendSeen(a, id)) write(a, SEEN, { playerId: playerId(a), id });
}

export function clockOffset(a: Ctx): ClockOffset | null {
  const c = read<ClockOffset>(a, CLOCK);
  return c && Number.isFinite(c.offset) && Number.isFinite(c.at) ? c : null;
}

export function saveClock(a: Ctx, c: ClockOffset): void {
  write(a, CLOCK, c);
}

/** Server time (the device clock corrected by the last measured offset). */
export function serverNow(a: Ctx): number {
  return a.services.now() + (clockOffset(a)?.offset ?? 0);
}
